import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { getAliceRecommendedAttributes, getAliceRecommendedEntities, importEntityAndAttributeInProject } from "../../../API/AliceGPT/AliceGPT";
import { Drawer, DrawerContent, DrawerItem } from "@progress/kendo-react-layout";
import InnerLoader from "../../Shared/Loader/InnerLoader";
import { Checkbox } from "@progress/kendo-react-inputs";
import AliceRecommendedAttributes from "./AliceRecommendedAttributes";
import { convertTypeToDesignerType } from "../../Helpers/Recommendation/AliceGPT";
import { getDatalakeImageUrl } from "../../Helpers/DateLakeStorage";
import FailedToLoadError from "../../Shared/Recommendation/Error/FailedToLoadError";
import { removeAttributeWithOtherEntityName } from "../../../Context/AliceGPT/utils";
import { useTranslation } from 'react-i18next';
import { icons } from "../../Shared/IconPicker/IconPicker";
import { OPENAI_MODEL } from "../../../Constants/OpenAI";

function AliceRecommendedEntitiesDialog({
    subscriptionId, project, existingEntityNames, organization,
    toggleDialog, onEntitiesAdded, onLimitReached
}) {

    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [fetchingMore, setFetchingMore] = useState(false);
    const [recommendedEntities, setRecommendedEntities] = useState([]);

    const [selectedEntity, setSelectedEntity] = useState({});
    const selectedEntityNameRef = useRef(null);

    const [creatingEntities, setCreatingEntities] = useState(false);

    const [error, setError] = useState("");

    useEffect(() => {
        getData();
    }, []);

    async function getData() {
        try {
            const recommendedEntities = await getEntities();
            const entitiesForDrawer = recommendedEntities.map(entityToStateArrayElement);
            entitiesForDrawer.forEach(entity => getAttributes(entity, entitiesForDrawer));
            setRecommendedEntities(entitiesForDrawer);
            setSelectedEntity(entitiesForDrawer[0]);
            selectedEntityNameRef.current = entitiesForDrawer[0].name;
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }

    const getEntities = useCallback(async () => {
        try {
            const response = await getAliceRecommendedEntities(
                project.domain || project.name, project.description, OPENAI_MODEL, existingEntityNames,
                project.language
            );
            const filteredEntities = response.filter(entity => !existingEntityNames.includes(entity[0]));
            if (filteredEntities.length > 0) {
                return filteredEntities;
            }
        } catch (error) {
            setError(error.message);
        }
    }, [])

    const getAttributes = useCallback(async (entity, recommendedEntities) => {
        const entityName = entity.name;
        try {
            const response = await getAliceRecommendedAttributes(
                entityName, OPENAI_MODEL, "", project.domain || project.name, project.description,
                project.language
            );
            const attributes = response.map(attributesToStateArrayElement);
            const entityNames = recommendedEntities.map(entity => entity.name).concat(existingEntityNames);
            const filteredAttributes = removeAttributeWithOtherEntityName(attributes, entityNames, entityName);
            handleAttributesLoaded(entityName, filteredAttributes);
            if (entityName === selectedEntityNameRef.current) {
                attributes.forEach(attribute => attribute.checked = true);
            }
            setRecommendedEntities(prevValue => [
                ...prevValue.map(entity => {
                    if (entity.name === entityName) {
                        return {
                            ...entity,
                            attributesLoading: false,
                            attributes: filteredAttributes.map(attr => ({
                                ...attr,
                                checked: entity.checked
                            })),
                            disabled: filteredAttributes.length === 0,
                            checked: false,
                            hasAttributesLoadingError: false
                        }
                    } else {
                        return entity;
                    }
                })
            ])
            if (selectedEntityNameRef.current === entityName) {
                setSelectedEntity(prevValue => ({
                    ...prevValue,
                    attributesLoading: false,
                    attributes: filteredAttributes.map(attr => ({
                        ...attr,
                        checked: prevValue.checked
                    })),
                    disabled: filteredAttributes.length === 0,
                    checked: false,
                    hasAttributesLoadingError: false
                }))
            }
        } catch (error) {
            setRecommendedEntities(prevValue => [
                ...prevValue.map(entity => {
                    if (entity.name === entityName) {
                        return {
                            ...entity,
                            disabled: true,
                            checked: false,
                            hasAttributesLoadingError: true,
                            attributesLoading: false
                        }
                    } else {
                        return entity;
                    }
                })
            ])
            if (selectedEntityNameRef.current === entityName) {
                setSelectedEntity(prevValue => ({
                    ...prevValue,
                    disabled: true,
                    checked: false,
                    hasAttributesLoadingError: true,
                    attributesLoading: false
                }))
            }
        }
    }, [])

    function entityToStateArrayElement(entity) {
        return {
            name: entity[0],
            icon: entity[1],
            checked: false,
            attributesLoading: true,
            attributes: [],
            hasAttributesLoadingError: false
        }
    }

    function attributesToStateArrayElement(attribute) {
        return {
            name: attribute[0],
            type: convertTypeToDesignerType(attribute[1]),
            picklist: attribute[2],
            unique: attribute[3],
            checked: false,
            selected: false
        }
    }

    function toDrawerItem(entity) {
        const isMoreThanTwoStateAttributesSelected = entity.attributes.filter(attribute => (
            attribute.type === "State Field" && attribute.checked
        )).length > 1;
        const hasStateOrDropdownWithNoValues = entity.attributes.some(attribute => (
            (attribute.type === "State Field" || attribute.type === "Picklist String") &&
            (!attribute.picklist || attribute.picklist.length === 0) &&
            attribute.checked
        ));
        const hasErrors = isMoreThanTwoStateAttributesSelected || hasStateOrDropdownWithNoValues;
        return {
            text: entity.name,
            disabled: entity.disabled,
            selected: entity.name === selectedEntity.name,
            checked: entity.checked,
            isUserEntity: entity.isUserEntity,
            attributesLoading: entity.attributesLoading,
            hasErrors
        }
    }

    function handleToggle() {
        toggleDialog();
    }

    function handleEntitySelect(event) {
        const entity = recommendedEntities[event.itemIndex - 1];
        setSelectedEntity(entity);
        selectedEntityNameRef.current = entity.name;
    }

    function handleEntityCheck(event) {
        selectedEntityNameRef.current = event.text;
        setRecommendedEntities(prevValue => prevValue.map(entity => {
            if (entity.name === event.text) {
                return {
                    ...entity,
                    checked: !entity.checked,
                    attributes: entity.attributes.map(attribute => ({
                        ...attribute,
                        checked: !entity.checked
                    }))
                }
            } else {
                return entity;
            }
        }))
        if (selectedEntityNameRef.current === event.text) {
            setSelectedEntity(prevValue => ({
                ...prevValue,
                checked: !prevValue.checked,
                attributes: prevValue.attributes.map(attribute => ({
                    ...attribute,
                    checked: !prevValue.checked
                }))
            }))
        }
    }

    function toggleSelectAllEntities() {
        const allEntitiesChecked = recommendedEntities.every(entity => entity.checked);
        setRecommendedEntities(prevValue => prevValue.map(entity => ({
            ...entity,
            checked: !allEntitiesChecked ? entity.attributes && entity.attributes.length > 0 : false,
            attributes: entity.attributes.map(attribute => ({
                ...attribute,
                checked: !allEntitiesChecked ? entity.attributes && entity.attributes.length > 0 : false
            }))
        })))
        if (selectedEntity) {
            setSelectedEntity(prevValue => ({
                ...prevValue,
                checked: !allEntitiesChecked ? prevValue.attributes && prevValue.attributes.length > 0 : false,
                attributes: prevValue.attributes.map(attribute => ({
                    ...attribute,
                    checked: !allEntitiesChecked ? prevValue.attributes && prevValue.attributes.length > 0 : false
                }))
            }))
        }
    }

    function toggleSelectAllEntityAttributes(entityName) {
        setRecommendedEntities(prevValue => [
            ...prevValue.map(entity => {
                if (entity.name === entityName) {
                    const allAttributesChecked = entity.attributes.every(attribute => attribute.checked);
                    return {
                        ...entity,
                        checked: !allAttributesChecked,
                        attributes: entity.attributes.map(attribute => ({
                            ...attribute,
                            checked: !allAttributesChecked
                        }))
                    }
                } else {
                    return entity;
                }
            })
        ])
        if (selectedEntity && selectedEntity.name === entityName) {
            const allAttributesChecked = selectedEntity.attributes.every(attribute => attribute.checked);
            setSelectedEntity(prevValue => ({
                ...prevValue,
                checked: !allAttributesChecked,
                attributes: prevValue.attributes.map(attribute => ({
                    ...attribute,
                    checked: !allAttributesChecked
                }))
            }))
        }
    }

    const handleAttributesLoaded = useCallback((entityName, attributes) => {
        if (attributes) {
            const entityNames = recommendedEntities.map(entity => entity.name).concat(existingEntityNames);
            const filteredAttributes = removeAttributeWithOtherEntityName(attributes, entityNames, entityName);
            setRecommendedEntities(prevValue => {
                const newRecommendedEntities = [...prevValue];
                const entityIndex = newRecommendedEntities.findIndex(entity => entity.name === entityName);
                newRecommendedEntities[entityIndex].attributes = filteredAttributes;
                newRecommendedEntities[entityIndex].disabled = filteredAttributes.length === 0;
                newRecommendedEntities[entityIndex].checked = false;
                return newRecommendedEntities;
            })
            if (selectedEntityNameRef.current === entityName) {
                setSelectedEntity(prevValue => ({
                    ...prevValue,
                    attributes: filteredAttributes,
                    disabled: filteredAttributes.length === 0,
                    checked: false
                }));
            }
        }
    }, []);

    // const handleAttributesAdded = useCallback((entityName, attributes) => {
    //     if (attributes) {
    //         setRecommendedEntities(prevValue.map(entity => {
    //             if (entity.name === entityName) {
    //                 return {
    //                     ...entity,
    //                     attributes: [
    //                         ...entity.attributes,
    //                         ...attributes
    //                     ]
    //                 }
    //             }
    //             return entity;
    //         }))
    //         if (selectedEntity.name === entityName) {
    //             setSelectedEntity(prevValue => ({
    //                 ...prevValue,
    //                 attributes: [
    //                     ...prevValue.attributes,
    //                     ...attributes
    //                 ]
    //             }))
    //         }
    //     }
    // }, []);

    function handleAttributeSelect(entityName, attributeIndex) {
        setRecommendedEntities(prevValue => prevValue.map(entity => {
            if (entity.name === entityName) {
                const attributes = entity.attributes.map((attribute, index) => {
                    if (index === attributeIndex) {
                        return {
                            ...attribute,
                            checked: !attribute.checked
                        }
                    }
                    return attribute;
                });
                const anyAttributeChecked = attributes.some(attribute => attribute.checked);
                return {
                    ...entity,
                    checked: anyAttributeChecked,
                    attributes: attributes
                }
            }
            return entity;
        }));
        if (selectedEntity.name === entityName) {
            setSelectedEntity(prevValue => ({
                ...prevValue,
                attributes: prevValue.attributes.map((attribute, index) => {
                    if (index === attributeIndex) {
                        return {
                            ...attribute,
                            checked: !attribute.checked
                        }
                    }
                    return attribute;
                }
                )
            }))
        }
    }

    async function handleSubmit(event) {
        try {
            event.target.disabled = true;

            setCreatingEntities(true);

            const selectedEntities = recommendedEntities.filter(entity => entity.checked);
            const selectedEntitiesWithAttributes = selectedEntities.map(entity => {
                const icon = entity.icon ? entity.icon : icons[Math.floor(Math.random() * icons.length)];
                return {
                    name: entity.name?.trim(),
                    icon,
                    attributes: entity.attributes.filter(attribute => attribute.checked).map(attr => {
                        let picklist = attr.picklist;
                        if (attr.type === "State Field" && attr.picklist.length === 0) {
                            picklist = ["Not Started", "In Progress", "Completed"];
                        }
                        return {
                            ...attr,
                            name: attr.name?.trim(),
                            required: attr.type === "State Field" ? true : attr.required,
                            picklist
                        }
                    })
                }
            });
            const response = await importEntityAndAttributeInProject(subscriptionId, organization._id, project._id, selectedEntitiesWithAttributes);
            if (response.status !== "Success") {
                if (response.data.length > 0) {
                    onEntitiesAdded(response.data);
                }
                onLimitReached();
            } else {
                onEntitiesAdded(response.data);
            }
            setCreatingEntities(false);
        } catch (error) {
            console.log(error);
        } finally {
            handleToggle();
        }
    }

    function handleShowMore() {
        setFetchingMore(true);
        const recommendedEntitiesToSkip = recommendedEntities.map(entity => entity.name);
        const entitiesToSkip = [...new Set([...existingEntityNames, ...recommendedEntitiesToSkip])].join(",");
        getAliceRecommendedEntities(
            project.domain, project.description, OPENAI_MODEL, entitiesToSkip,
            project.language
        )
            .then(response => {
                const entities = response.map(entityToStateArrayElement);
                entities.forEach(entity => getAttributes(entity, recommendedEntities.concat(entities)));
                setRecommendedEntities(prevValue => [...prevValue, ...entities]);
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                setFetchingMore(false);
            })
    }

    function handlePicklistUpdate(entityName, attributeName, picklist) {
        setRecommendedEntities(prevValue => prevValue.map(entity => {
            if (entity.name === entityName) {
                return {
                    ...entity,
                    attributes: entity.attributes.map(attribute => {
                        if (attribute.name === attributeName) {
                            return {
                                ...attribute,
                                picklist
                            }
                        }
                        return attribute;
                    })
                }
            }
            return entity;
        }))
        if (selectedEntity.name === entityName) {
            setSelectedEntity(prevValue => ({
                ...prevValue,
                attributes: prevValue.attributes.map(attribute => {
                    if (attribute.name === attributeName) {
                        return {
                            ...attribute,
                            picklist
                        }
                    }
                    return attribute;
                })
            }))
        }
    }

    function handleAttributeTypeChange(entityName, attributeName, type) {
        setRecommendedEntities(prevValue => prevValue.map(entity => {
            if (entity.name === entityName) {
                return {
                    ...entity,
                    attributes: entity.attributes.map(attribute => {
                        if (attribute.name === attributeName) {
                            return {
                                ...attribute,
                                type,
                                picklist: attribute.picklist ? attribute.picklist : ["Value 1"]
                            }
                        }
                        return attribute;
                    })
                }
            }

            return entity;
        }))
        if (selectedEntity.name === entityName) {
            setSelectedEntity(prevValue => ({
                ...prevValue,
                attributes: prevValue.attributes.map(attribute => {
                    if (attribute.name === attributeName) {
                        return {
                            ...attribute,
                            type,
                            picklist: attribute.picklist ? attribute.picklist : ["Value 1"]
                        }
                    }
                    return attribute;
                })
            }))
        }
    }

    function shouldAddBeDisabled() {
        const selectedEntities = recommendedEntities.filter(entity => entity.checked);
        if (selectedEntities.length === 0) {
            return true;
        }
        const selectedEntitiesWithAttributesLoading = selectedEntities.filter(entity => entity.attributesLoading);
        if (selectedEntitiesWithAttributesLoading.length > 0) {
            return true;
        }
        const selectedAttributes = selectedEntities.map(entity => entity.attributes.filter(attribute => attribute.checked));
        if (selectedAttributes.some(attributes => attributes.length === 0)) {
            return true;
        }
        const isMoreThanTwoStateAttributesSelected = selectedEntities.some(entity => (
            entity.attributes.filter(attribute => (
                attribute.type === "State Field" && attribute.checked
            )).length > 1
        ));
        const hasStateOrDropdownWithNoValues = selectedEntities.some(entity => (
            entity.attributes.some(attribute => (
                (attribute.type === "State Field" || attribute.type === "Picklist String") &&
                (!attribute.picklist || attribute.picklist.length === 0) &&
                attribute.checked
            ))
        ));
        if (isMoreThanTwoStateAttributesSelected || hasStateOrDropdownWithNoValues) {
            return true;
        }
        return false;
    }

    return (
        <Dialog
            title={t("entity.recommendation_dialog.title")}
            onClose={handleToggle}
            height={"80%"}
            width={"70%"}
        >
            {
                loading &&
                <div className="h-100 w-100 d-flex flex-column justify-content-center align-items-center">
                    <img src={getDatalakeImageUrl("/loaders/RecommendationLoader.gif")} alt="" />
                    <h3 className="mt-4">{t("entity.recommendation_dialog.loading_message")}</h3>
                </div>
            }
            {
                !loading &&
                error.length > 0 &&
                <FailedToLoadError
                    error={t("recommendation_dialog.errors.failed_to_load_error")}
                />
            }
            {
                !loading &&
                error.length === 0 &&
                <Drawer
                    expanded
                    position='start'
                    mode='push'
                    onSelect={handleEntitySelect}
                    items={
                        [
                            {
                                text: t("entity.recommendation_dialog.select_all_entities_label"),
                                isHeading: true,
                                onClick: toggleSelectAllEntities
                            },
                            ...recommendedEntities.map(toDrawerItem),
                            {
                                text: t("entity.recommendation_dialog.show_more_label"),
                                selected: false,
                                checked: false,
                                isShowMoreOption: true,
                                onClick: handleShowMore,
                            }
                        ]
                    }
                    item={(props) => {
                        const { disabled, ...otherProps } = props;
                        return (
                            <DrawerItem
                                {...otherProps}
                                className='d-flex align-items-center p-3'
                            >
                                {
                                    props.isHeading &&
                                    <h5 className="d-flex align-items-center">
                                        <Checkbox
                                            checked={recommendedEntities.every(entity => entity.checked)}
                                            value={recommendedEntities.every(entity => entity.checked)}
                                            onChange={() => { }}
                                        />
                                        <span className="mb-0 ps-2 fw-700">{props.text} </span>
                                    </h5>
                                }
                                {
                                    props.isShowMoreOption &&
                                    <>
                                        <button className='btn btn-link font-size-2' disabled={fetchingMore}>
                                            {t("entity.recommendation_dialog.show_more_label")}
                                        </button>
                                        {
                                            fetchingMore ?
                                                <InnerLoader borderColor="green" />
                                                :
                                                <>&nbsp;</>
                                        }
                                    </>
                                }
                                {!props.isHeading && !props.isShowMoreOption &&
                                    <>
                                        <Checkbox
                                            disabled={props.disabled}
                                            checked={props.checked}
                                            value={props.checked}
                                            onChange={() => handleEntityCheck(props)}
                                        />
                                        &nbsp;
                                        <span className="ms-1">
                                            {props.text}
                                        </span>
                                        {
                                            props.attributesLoading &&
                                            <>
                                                &nbsp;
                                                <InnerLoader borderColor="blue" />
                                            </>
                                        }
                                    </>
                                }
                                &nbsp;
                                {
                                    props.hasErrors &&
                                    <i className="text-danger fa-solid fa-circle-info"></i>
                                }
                            </DrawerItem>
                        )
                    }}
                >
                    <DrawerContent className='h-100'>
                        {
                            selectedEntity &&
                            <AliceRecommendedAttributes
                                entity={selectedEntity}
                                projectDomain={project.domain}
                                projectDescription={project.description}
                                projectLanguage={project.language}
                                onAttributesLoaded={handleAttributesLoaded}
                                onToggleAllAttributes={toggleSelectAllEntityAttributes}
                                onSelect={handleAttributeSelect}
                                onPicklistUpdate={handlePicklistUpdate}
                                onAttributeTypeChange={handleAttributeTypeChange} />
                        }
                    </DrawerContent>
                </Drawer>
            }
            {
                !loading &&
                <DialogActionsBar layout="end">
                    <div className='d-flex align-items-center mb-4 me-3 pe-2'>
                        {
                            recommendedEntities.some(entity => (
                                entity.attributes.filter(
                                    attribute => attribute.type === "State Field" && attribute.checked
                                ).length > 1
                            )) &&
                            <span className="text-danger">
                                {t("entity.recommendation_dialog.errors.more_than_two_state_attributes_selected")}
                            </span>
                        }
                        {
                            creatingEntities &&
                            <InnerLoader borderColor="green" />
                        }
                        <button className="btn" onClick={handleToggle}>{t("entity.recommendation_dialog.buttons.cancel_label")}</button>
                        <button className="btn btn-primary" onClick={handleSubmit} disabled={shouldAddBeDisabled()}>{t("entity.recommendation_dialog.buttons.add_label")}</button>
                    </div>
                </DialogActionsBar>
            }
        </Dialog>
    )
}

export default AliceRecommendedEntitiesDialog;